// src/pages/Documentos/Documentos.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
    Paper, Tabs, Tab, TextField, InputAdornment, IconButton, Select,
    MenuItem, FormControl, InputLabel, Button, Chip, List, ListItem,
    ListItemText, ListItemSecondaryAction, Tooltip, CircularProgress,
    Typography, Switch, FormControlLabel, Dialog, DialogTitle,
    DialogContent, DialogActions, Menu, Badge, Divider, Avatar,
    ListItemAvatar, Collapse, Box, Grid, Drawer,
    Card, CardActions, CardContent, CardHeader, CardActionArea,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FolderIcon from "@mui/icons-material/Folder";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import GetAppIcon from "@mui/icons-material/GetApp";
import PublishIcon from "@mui/icons-material/Publish";
import AssessmentIcon from "@mui/icons-material/Assessment";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { Autocomplete } from '@mui/material';

import {
    documentosCategoriasJWT, documentosListarJWT, documentosGetJWT,
    documentosSaveJWT, documentosDeleteJWT, documentosFavoritoJWT,
    documentosCompartidosGetJWT, documentosCompartirSetJWT, documentosUnshareJWT,
    documentosPersonalCompartirJWT, documentosVarsJWT, documentosPreviewJWT,
    documentosUserInfoJWT,
} from "../../api/endpoints";
import TinyEditor from "../../components/TinyEditor";
import { Alert, Skeleton, Snackbar, ToggleButton, ToggleButtonGroup } from '@mui/material';
import WarningIcon from "@mui/icons-material/Warning";
import { usePermisos } from "../../utils/usePermisos";

const formatFecha = (fecha) => {
    if (!fecha) return '';
    try {
        const d = new Date(fecha);
        if (isNaN(d)) return fecha;
        return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return fecha; }
};

// ---------- paleta Documentos ----------
const DOCS_PRIMARY = '#2e7d6f';       // teal - header, íconos principales
const DOCS_PRIMARY_DARK = '#1b5e50';  // teal oscuro - hover
// ---------- estilos mejorados ----------
const styles = {
    root: {
        display: "grid",
        gap: '16px',
        height: "calc(100vh - 112px)",
        '@media (max-width: 1366px)': { gap: '12px' },
    },

    resizer: {
        width: 8,
        cursor: "col-resize",
        background: "transparent",
        transition: "background 0.15s",
        borderRadius: '4px',
        alignSelf: "stretch",
        zIndex: 5,
        '&:hover': { background: "rgba(0,0,0,0.20)" }
    },

    paperPanel: {
        borderRadius: '4px',
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
        border: "1px solid rgba(255,255,255,0.3)",
    },

    panelHeader: {
        background: DOCS_PRIMARY,
        padding: "8px 12px",
        color: "#fff",
        '@media (max-width: 1366px)': { padding: "10px 12px" },
    },

    panelTitleFix: { fontWeight: 600 },
    panelSubtitleFix: { opacity: 0.85 },

    tabsContainer: {
        background: "#fff",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "3px 3px 0 0"
        }
    },

    listToolbar: {
        padding: "8px 12px",
        background: "#fafbfc",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        display: "flex",
        gap: '8px',
        alignItems: "center"
    },

    searchField: {
        flex: 1,
        minWidth: 250,
        "& .MuiOutlinedInput-root": {
            borderRadius: '4px',
            background: "#fff",
            height: 40,
            "&:hover fieldset": {
                borderColor: "#1976d2"
            }
        },
        "& .MuiOutlinedInput-input": {
            padding: "10px 14px",
        },
        '@media (max-width: 1366px)': { "& .MuiOutlinedInput-root": { height: 36 } },
    },

    listBox: {
        flex: 1,
        overflow: "auto",
        background: "#fff",
        "&::-webkit-scrollbar": {
            width: 8
        },
        "&::-webkit-scrollbar-track": {
            background: "#f1f1f1"
        },
        "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: '4px',
            "&:hover": {
                background: "#555"
            }
        }
    },

    listItem: {
        padding: "10px 12px",
        transition: "all 0.2s",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        "&:hover": {
            background: "rgba(103, 126, 234, 0.05)"
        },
        "&.Mui-selected": {
            background: "linear-gradient(90deg, rgba(103,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)",
            borderLeft: "3px solid #667eea"
        }
    },

    itemIcon: {
        marginRight: '12px',
        width: 36,
        height: 36,
        borderRadius: '10px',
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: DOCS_PRIMARY,
        color: "#fff"
    },

    itemBadge: {
        fontSize: 11,
        padding: "2px 8px",
        borderRadius: '10px',
        fontWeight: 600
    },

    footerBar: {
        padding: "12px 16px",
        borderTop: "1px solid rgba(0,0,0,0.12)",
        background: "#fafbfc",
        display: "flex",
        gap: '8px'
    },

    previewPanel: {
        borderRadius: '4px',
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
        border: "1px solid rgba(255,255,255,0.3)"
    },

    previewHeader: {
        background: DOCS_PRIMARY,
        padding: "8px 12px",
        color: "#fff",
        position: "sticky",
        top: 0,
        zIndex: 2
    },

    previewToolbar: {
        padding: "12px 16px",
        background: "#fafbfc",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        display: "flex",
        gap: '12px',
        alignItems: "center",
        flexWrap: "wrap"
    },

    varsBox: {
        padding: "12px 16px",
        background: "#f8f9fa",
        borderBottom: "1px solid rgba(0,0,0,0.12)",
        display: "flex",
        flexWrap: "wrap",
        gap: '6px',
        maxHeight: 80,
        overflow: "auto"
    },

    varChip: {
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)"
        }
    },

    previewBox: {
        flex: 1,
        overflow: "auto",
        background: "linear-gradient(to bottom, #fafbfc, #f0f2f5)",
        "&::-webkit-scrollbar": {
            width: 12
        },
        "&::-webkit-scrollbar-track": {
            background: "#f1f1f1"
        },
        "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: '6px',
            "&:hover": {
                background: "#555"
            }
        }
    },

    a4Page: {
        width: "210mm",
        minHeight: "297mm",
        margin: "20px auto",
        background: "#fff",
        boxShadow: "0 10px 50px rgba(0,0,0,0.1)",
        borderRadius: '4px',
        overflow: "hidden",
        '@media (max-width: 1366px)': { margin: "12px auto" },
    },

    a4Inner: {
        padding: "25mm 20mm",
        fontFamily: "Times New Roman, Times, serif",
        color: "#222",
        fontSize: "12pt",
        minHeight: "247mm",
        '& p': {
            margin: '0 0 1em 0',
        },
        '& p p': {
            display: 'inline',
        },
        '& div div': {
            display: 'block',
        }
    },

    emptyState: {
        padding: '40px',
        textAlign: "center",
        color: "rgba(0, 0, 0, 0.6)",
        "& svg": {
            fontSize: 60,
            opacity: 0.3,
            marginBottom: '16px'
        }
    },

    editorDialog: {
        "& .MuiDialog-paper": {
            maxWidth: 1200,
            width: "95vw",
            height: "92vh",
            borderRadius: '4px',
            display: "flex",
            flexDirection: "column"
        }
    },

    editorHeader: {
        background: DOCS_PRIMARY,
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: '12px'
    },

    editorTitle: {
        flex: 1,
        "& .MuiInputBase-root": {
            color: "#fff",
            fontSize: 20,
            fontWeight: 600
        },
        "& .MuiInput-underline:before": {
            borderBottomColor: "rgba(255,255,255,0.3)"
        },
        "& .MuiInput-underline:after": {
            borderBottomColor: "#fff"
        },
        "& .MuiInputLabel-root": {
            color: "rgba(255,255,255,0.7)"
        }
    },

    editorContent: {
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
    },

    editorFooter: {
        padding: "12px 24px",
        background: "#fafbfc",
        borderTop: "1px solid rgba(0,0,0,0.12)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: '12px',
    },

    primaryButton: {
        background: DOCS_PRIMARY,
        color: "#fff",
        borderRadius: '4px',
        padding: "10px 20px",
        fontWeight: 600,
        textTransform: "none",
        boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)"
        }
    },

    secondaryButton: {
        borderRadius: '4px',
        padding: "10px 20px",
        fontWeight: 600,
        textTransform: "none",
        border: "2px solid",
        borderColor: "#1976d2"
    },

    ghostButton: {
        color: "#fff",
        background: "transparent",
        border: "1px solid rgba(255,255,255,.9)",
        borderRadius: '4px',
        padding: "8px 14px",
        fontWeight: 600,
        textTransform: "none",
        boxShadow: "none",
        transition: "background .15s ease, transform .12s ease",
        "&:hover": {
            background: "rgba(255,255,255,.12)",
            boxShadow: "none",
            transform: "translateY(-1px)"
        },
        "&:active": { transform: "translateY(0)" },
        "& .MuiButton-startIcon": { marginRight: '8px' }
    },

    ghostButtonLight: {
        color: "#1565c0",
        border: "2px solid #1565c0",
        background: "transparent",
        "&:hover": { background: "rgba(21,101,192,.08)" }
    },

    plusBadge: {
        width: 22,
        height: 22,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: '999px',
        border: "0px solid rgba(255,255,255,.9)",
    },

    loadingOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(255,255,255,0.95)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        borderRadius: '4px'
    },

    backdrop: {
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,.35)",
        backdropFilter: "blur(1.5px)",
        zIndex: 1199,
        animation: "docFadeIn .12s ease-out",
    },

    previewOverlay: {
        position: "fixed",
        top: 80,
        right: 24,
        bottom: 24,
        width: "clamp(420px, 70vw, 760px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: '4px',
        overflow: "hidden",
        zIndex: 1200,
        background: "rgba(255,255,255,.82)",
        backdropFilter: "blur(6px) saturate(120%)",
        border: "1px solid rgba(255,255,255,.35)",
        boxShadow: "0 18px 60px rgba(0,0,0,.25)",
        animation: "docSlideIn .18s ease-out",
    },

    closeGhost: {
        color: "#fff",
        border: "1px solid rgba(255,255,255,.9)",
        borderRadius: '4px',
        padding: '6px',
        "&:hover": { background: "rgba(255,255,255,.12)" }
    },

    viewToggle: {
        borderRadius: '4px',
        border: '1px solid rgba(0,0,0,0.12)',
        background: 'transparent',
        marginLeft: '8px',
        '& .MuiToggleButton-root': {
            padding: '4px 10px',
            border: 'none',
        },
        '& .MuiToggleButton-root.Mui-selected': {
            background: 'rgba(21,101,192,0.08)',
        },
        '& .MuiToggleButton-root:hover': {
            background: 'rgba(0,0,0,0.04)',
        }
    },

    documentCard: {
        height: "100%",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        borderRadius: '4px',
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 24px rgba(0,0,0,0.15)"
        },
    },

    documentCardSelected: {
        border: "2px solid #1976d2",
        background: "rgba(0, 0, 0, 0.08)"
    },

    cardAvatar: {
        background: DOCS_PRIMARY,
        color: "#fff"
    },

    gridContainer: {
        padding: '16px',
        height: "100%",
        overflow: "auto"
    },

    advancedSearchBox: {
        background: "#f8f9fa",
        borderRadius: '4px',
        padding: '16px',
        marginTop: '16px',
        border: "1px solid rgba(0,0,0,0.12)"
    },

    speedDial: {
        position: 'fixed', bottom: 16, right: 16, zIndex: 1201,
        '& .MuiFab-primary': { backgroundColor: DOCS_PRIMARY, '&:hover': { backgroundColor: DOCS_PRIMARY_DARK } }
    },

    speedDialAction: {
        background: '#fff',
        '& .MuiSpeedDialAction-staticTooltipLabel': {
            background: '#1e293b',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '4px'
        },
        '&:hover': { background: DOCS_PRIMARY, color: '#fff' }
    },

    dashboardButton: {
        position: "absolute",
        top: 16,
        right: 16,
        background: "rgba(255,255,255,0.9)",
        "&:hover": {
            background: "#fff"
        }
    },

    templateCard: {
        padding: '16px',
        cursor: "pointer",
        border: "2px dashed rgba(0,0,0,0.12)",
        borderRadius: '4px',
        textAlign: "center",
        transition: "all 0.2s",
        "&:hover": {
            borderColor: "#1976d2",
            background: "rgba(0, 0, 0, 0.04)"
        }
    },

    sdActionSx: {
        "&:hover .MuiSpeedDialAction-staticTooltipLabel": {
            transform: "translateX(-4px)",
            boxShadow: "0 10px 28px rgba(0,0,0,.22)",
        },
        "&:hover .MuiSpeedDialAction-fab": {
            transform: "translateY(-1px)",
            backgroundColor: DOCS_PRIMARY,
            color: "#fff",
        },
        transition: "transform .15s ease",
        "& .MuiSpeedDialAction-staticTooltipLabel": {
            whiteSpace: "nowrap",
            padding: "8px 14px",
            borderRadius: '4px',
            fontWeight: 600,
            fontSize: 13,
            background: DOCS_PRIMARY,
            color: "#fff",
            border: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,.15)",
            maxWidth: "unset",
            transition: "transform .15s ease, box-shadow .15s ease",
        },
        "& .MuiSpeedDialAction-fab": {
            width: 42,
            height: 42,
            backgroundColor: "#fff",
            color: DOCS_PRIMARY,
            border: `2px solid ${DOCS_PRIMARY}`,
            boxShadow: "0 2px 8px rgba(0,0,0,.1)",
            transition: "all .15s ease",
        },
    },

    sdGlass: {
        position: "fixed",
        right: 16,
        bottom: 16,
        width: 240,
        height: 320,
        borderRadius: '4px',
        background: "rgba(255,255,255,.72)",
        border: "1px solid rgba(255,255,255,.35)",
        backdropFilter: "blur(8px) saturate(140%)",
        boxShadow: "0 18px 60px rgba(0,0,0,.25)",
        zIndex: 999,
        pointerEvents: "none",
    },
};

// Tabs configuración
const ALL_TABS = [
    { key: "todos", label: "Públicos", icon: <FolderIcon />, requierePermiso: true },
    { key: "mios", label: "Mis Escritos", icon: <PersonIcon /> },
    { key: "compartidos", label: "Compartidos", icon: <ShareIcon /> },
    { key: "favoritos", label: "Favoritos", icon: <StarIcon /> },
    { key: "borradores", label: "Borradores", icon: <EditIcon /> },
];

// Hook para debounce
function useDebounced(value, delay) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay || 350);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

export default function Documentos() {
    // styles object defined above (no useStyles needed)
    const rootRef = useRef(null);
    const permisos = usePermisos();

    const [categorias, setCategorias] = useState([]);
    const [editorCategoriaId, setEditorCategoriaId] = useState(null);
    const [editorCategoria, setEditorCategoria] = useState("");

    // Snackbar para notificaciones
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });
    const showSnack = (message, severity = 'error') => setSnack({ open: true, message, severity });

    // Dialog para confirmar salir sin guardar
    const [confirmSalir, setConfirmSalir] = useState({ open: false, onConfirm: null });

    // Obtener usuario actual
    const [usuarioActual, setUsuarioActual] = useState("");

    useEffect(() => {
        const fetchUsuario = async () => {
            try {
                const res = await documentosUserInfoJWT();
                if (res?.ok && res.usuario) {
                    // res.usuario puede ser un objeto o string
                    if (typeof res.usuario === 'string') {
                        setUsuarioActual(res.usuario);
                    } else if (res.usuario.usuario) {
                        setUsuarioActual(res.usuario.usuario);
                    } else if (res.usuario.nombre) {
                        setUsuarioActual(res.usuario.nombre);
                    }
                }
            } catch {
                // Silencioso
            }
        };
        fetchUsuario();
    }, []);

    useEffect(() => {
        let mounted = true;

        async function fetchCategorias() {
            try {
                const data = await documentosCategoriasJWT();
                if (!mounted) return;
                setCategorias(data?.categorias || []);
            } catch (e) {
                console.error("Error cargando categorías de escritos:", e);
                // opcional: openSnack("Error cargando categorías.", "error");
            }
        }

        fetchCategorias();
        return () => { mounted = false; };
    }, []);

    const permisosGlobales = useMemo(() => {
        if (!permisos) return { puedeCrear: false, puedeVerTodos: false };

        let puedeCrear = false;
        let puedeVerTodos = false;


        // El objeto permisos tiene una propiedad .permisos que contiene los módulos
        const permisosModulos = permisos.permisos || permisos;

        for (const nombreModulo in permisosModulos) {
            const moduloPermisos = permisosModulos[nombreModulo];
            const bibPermisos = moduloPermisos?.["BIBLIOTECA DE DOCUMENTOS RESTRINGIDOS"];


            if (bibPermisos?.["INSERT"] === true) {
                puedeCrear = true;
            }
            if (bibPermisos?.["SELECT"] === true) {
                puedeVerTodos = true;
            }
        }

        return { puedeCrear, puedeVerTodos };
    }, [permisos]);

    const puedeCrearGlobal = permisosGlobales.puedeCrear;
    const puedeVerTodos = permisosGlobales.puedeVerTodos;

    const TABS = useMemo(() => {
        return ALL_TABS.filter(t => {
            if (t.key === "todos" && !puedeVerTodos) return false;
            return true;
        });
    }, [puedeVerTodos]);

    const [viewMode, setViewMode] = useState(() => {
        return localStorage.getItem("doc-viewMode") || "list";
    });

    const [editorActivo, setEditorActivo] = useState(true);
    const [catFilter, setCatFilter] = useState("todos");

    useEffect(() => {
        localStorage.setItem("doc-viewMode", viewMode);
    }, [viewMode]);

    const [advancedFilters] = useState({
        categoria: '',
        autor: '',
        fechaDesde: null,
        fechaHasta: null
    });

    // ===== COMPARTIR (por Id de Personal) =====
    const [shareOpen, setShareOpen] = useState(false);
    const [shareDoc, setShareDoc] = useState(null);
    const [shareSelectedIds, setShareSelectedIds] = useState([]);
    const [shareQuery, setShareQuery] = useState("");
    const shareQueryDeb = useDebounced(shareQuery, 350);
    const [shareOptions, setShareOptions] = useState([]);
    const [shareLoading, setShareLoading] = useState(false);
    const [shareSearchLoading, setShareSearchLoading] = useState(false);
    const [shareSaving, setShareSaving] = useState(false);
    const [shareNameMap, setShareNameMap] = useState({});

    const shareOptionsComputed = React.useMemo(() => {
        const map = new Map(shareOptions.map(o => [String(o.id), o]));
        for (const id of shareSelectedIds) {
            const k = String(id);
            if (!map.has(k)) map.set(k, { id, nombre: String(id) });
        }
        return Array.from(map.values());
    }, [shareOptions, shareSelectedIds]);

    const openShareDialog = async (item) => {
        setShareDoc(item);
        setShareSelectedIds([]);
        setShareQuery("");
        setShareOpen(true);
        setShareLoading(true);
        try {
            const res = await documentosCompartidosGetJWT(item.id);
            if (res?.ok) {
                const ids = Array.isArray(res.personalIds) ? res.personalIds.map(Number).filter(Number.isFinite) : [];
                setShareSelectedIds(ids);

                const next = { ...shareNameMap };
                if (Array.isArray(res.personas)) {
                    for (const p of res.personas) {
                        const pid = Number(p.id);
                        const nombre = String(p.nombre || "").trim();
                        if (Number.isFinite(pid) && nombre) next[pid] = nombre;
                    }
                }

                for (const pid of ids) {
                    if (!next[pid]) {
                        try {
                            const resPers = await documentosPersonalCompartirJWT(null, pid);
                            if (resPers?.ok && resPers.nombre) {
                                next[pid] = resPers.nombre;
                            } else {
                                next[pid] = `Usuario #${pid}`;
                            }
                        } catch {
                            next[pid] = `Usuario #${pid}`;
                        }
                    }
                }

                setShareNameMap(next);
            } else {
                showSnack(res?.mensaje || "No se pudo cargar los usuarios compartidos", "error");
            }
        } catch (e) {
            showSnack("Error de red al cargar compartidos", "error");
        } finally {
            setShareLoading(false);
        }
    };

    const saveShare = async () => {
        if (!shareDoc) return;

        setShareSaving(true);
        try {
            const uniqueIds = Array.from(new Set((shareSelectedIds || []).map(Number).filter(Number.isFinite)));
            const res = await documentosCompartirSetJWT(shareDoc.id, uniqueIds);

            if (!res?.ok) {
                throw new Error(res?.mensaje || "No se pudo guardar los compartidos");
            }

            setShareOpen(false);
            await loadList();
        } catch (e) {
            showSnack(e?.message || "Error de red al guardar compartidos", "error");
        } finally {
            setShareSaving(false);
        }
    };

    // ===== BUSCADOR DE USUARIOS =====
    const fetchPersonal = useCallback(async (q) => {
        if (!q || q.length < 2) { setShareOptions([]); return; }
        setShareSearchLoading(true);
        try {
            const res = await documentosPersonalCompartirJWT(q);
            if (res?.ok) {
                const items = (res.items || []).map(x => ({
                    id: Number(x.id),
                    nombre: String(x.nombre || "").trim(),
                })).filter(x => Number.isFinite(x.id) && x.nombre);

                setShareOptions(items);

                setShareNameMap(prev => {
                    const next = { ...prev };
                    for (const it of items) if (it.nombre) next[it.id] = it.nombre;
                    return next;
                });
            } else {
                setShareOptions([]);
            }
        } catch {
            setShareOptions([]);
        } finally {
            setShareSearchLoading(false);
        }
    }, []);

    useEffect(() => { fetchPersonal(shareQueryDeb); }, [shareQueryDeb, fetchPersonal]);

    // Estados principales
    const [tab, setTab] = useState(0);
    const [q, setQ] = useState("");
    const qDeb = useDebounced(q, 400);
    const [modulo, setModulo] = useState("todos");
    const [cargando, setCargando] = useState(false);
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const expediente = "";

    const [speedDialOpen, setSpeedDialOpen] = useState(false);

    const [vars, setVars] = useState([]);
    const [prevLoading, setPrevLoading] = useState(false);
    const [prevHtml, setPrevHtml] = useState("");

    const [editorOpen, setEditorOpen] = useState(false);
    const [editorId, setEditorId] = useState(0);
    const [editorTitulo, setEditorTitulo] = useState("");
    const [editorDescripcion, setEditorDescripcion] = useState("");
    const [editorScope, setEditorScope] = useState("PRIVATE");
    const [editorHtml, setEditorHtml] = useState("");
    const [editorEstado, setEditorEstado] = useState("BORRADOR");
    const [showVarsBar, setShowVarsBar] = useState(false);
    const [saving, setSaving] = useState(false);
    const editorApiRef = React.useRef(null);

    const [showFilters, setShowFilters] = useState(false);
    const [showVarsPreview] = useState(false);
    const [favorites] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const [unshareConfirmOpen, setUnshareConfirmOpen] = useState(false);
    const [itemToUnshare, setItemToUnshare] = useState(null);

    const [contextMenu, setContextMenu] = useState(null);
    const [contextItem, setContextItem] = useState(null);

    const [showPreview, setShowPreview] = useState(() => {
        const v = localStorage.getItem("doc-showPreview");
        // Si es la primera vez y no hay nada guardado, que empiece cerrado
        if (v === null) return false;
        return v === "1";
    });

    const [leftWidth, setLeftWidth] = useState(() => {
        const v = Number(localStorage.getItem("doc-leftWidth"));
        return Number.isFinite(v) && v >= 280 ? v : 680;
    });

    const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= 1366);

    // ===== FUNCION DE PERMISOS =====
    const puedeRealizarAccion = useCallback((item, accion) => {
        if (!item || !usuarioActual) return false;

        // Normalizamos la estructura de permisos (puede venir como {permisos:{...}} o directamente como objeto)
        const permisosModulos = permisos?.permisos || permisos || {};

        // Calcular tabActual sin depender de TABS
        const tabsVisibles = ALL_TABS.filter(t => !(t.key === "todos" && !puedeVerTodos));
        const tabActual = tabsVisibles[tab]?.key;

        // Comparar con OwnerUsuario que es el username real
        const esOwner = (
            item.OwnerUsuario === usuarioActual ||
            item.ownerUsuario === usuarioActual ||
            item.Usuario === usuarioActual ||
            item.UsuarioCreador === usuarioActual ||
            item.creador === usuarioActual
        );
        const scope = ((item.scope || item.Scope || "") + "").toUpperCase();
        const esBorrador = item.estado === "BORRADOR" || item.Estado === "BORRADOR";
        const esCompartido = item.sharedWithMe || item.SharedWithMe;

        const puedeGlobal = (accionSolicitada) => {
            const esCartaPoder = (item.categoria || "").toLowerCase().includes("carta poder");
            const funcionPermiso = esCartaPoder ? "CARTA PODER" : "BIBLIOTECA DE DOCUMENTOS RESTRINGIDOS";
            const mapAccion = {
                view: "SELECT",
                create: "INSERT",
                edit: "UPDATE",
                delete: "DELETE",
                share: "SELECT",
                favorite: "SELECT",
                duplicate: "SELECT",
            };
            const permiso = mapAccion[accionSolicitada];
            if (!permiso) return false;

            for (const modulo in permisosModulos) {
                const mp = permisosModulos[modulo];
                if (mp?.[funcionPermiso]?.[permiso] === true) return true;
            }
            return false;
        };

        // Owner: permite todas las acciones salvo "unshare-self"
        if (esOwner) {
            if (accion === "unshare-self") return false;
            return true;
        }

        // No compartir borradores
        if (accion === "share" && esBorrador) return false;

        // LOGICA POR TAB
        if (tabActual === "todos") {
            if (accion === "delete") return false; // eliminar global solo el owner, ya filtrado arriba
            if (scope === "GLOBAL") return puedeGlobal(accion);
            return false;
        }

        if (tabActual === "favoritos") {
            if (accion === "unshare-self") return false;
            if (esCompartido) return ["view", "edit", "share", "favorite", "duplicate"].includes(accion);
            return false;
        }

        if (tabActual === "borradores") {
            return false; // solo owner, ya filtrado arriba
        }

        if (tabActual === "compartidos") {
            if (accion === "delete" || accion === "share") return false;
            if (accion === "unshare-self") return true;
            return ["view", "edit", "favorite", "duplicate"].includes(accion);
        }

        if (tabActual === "mios") {
            return false; // solo owner, ya filtrado arriba
        }

        // LOGICA GENERAL (fuera de tabs especiales)
        if (esCompartido && tabActual !== "compartidos") {
            if (["delete", "share", "unshare-self"].includes(accion)) return false;
            return ["view", "edit", "favorite", "duplicate"].includes(accion);
        }

        if (scope === "GLOBAL") return puedeGlobal(accion);

        return false;
    }, [usuarioActual, permisos, tab, puedeVerTodos]);

    // Cargador perezoso de mammoth
    async function ensureMammoth() {
        if (window.mammoth) return;
        await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://unpkg.com/mammoth@1.6.0/mammoth.browser.min.js";
            s.onload = resolve;
            s.onerror = () => reject(new Error("No se pudo cargar mammoth.js"));
            document.head.appendChild(s);
        });
    }

    async function ensureHtml2pdfUniversal() {
        if (typeof window.process === "undefined") {
            window.process = {
                env: { NODE_ENV: 'production' },
                version: '',
                versions: {}
            };
        }

        if (window.html2pdf) return;

        const candidates = [
            "/libs/html2pdf.bundle.min.js",
            "/Ejecuciones/libs/html2pdf.bundle.min.js",
            "https://unpkg.com/html2pdf.js@0.10.1/dist/html2pdf.bundle.min.js"
        ];

        await new Promise(function (resolve, reject) {
            var i = 0;
            function loadNext() {
                if (i >= candidates.length) return reject(new Error("No se pudo cargar html2pdf"));
                var s = document.createElement("script");
                s.src = candidates[i++];
                s.onload = resolve;
                s.onerror = function () { s.remove(); loadNext(); };
                document.head.appendChild(s);
            }
            loadNext();
        });
    }

    async function exportHTMLtoPDF(opts) {
        opts = opts || {};
        var title = opts.title || "documento";
        var html = opts.html || "";
        var marginMm = typeof opts.marginMm === "number" ? opts.marginMm : 18;

        await ensureHtml2pdfUniversal();

        var wrapper = document.createElement("div");
        wrapper.style.position = "fixed";
        wrapper.style.left = "-100000px";
        wrapper.style.top = "0";
        wrapper.innerHTML =
            '<div id="__pdf_a4__" style="width:210mm;min-height:297mm;padding:' + marginMm + 'mm;background:#fff;color:#222;box-sizing:border-box;font-family:Calibri, Arial, sans-serif;line-height:1.45;">'
            + html +
            "</div>";
        document.body.appendChild(wrapper);

        var opt = {
            margin: [0, 0, 0, 0],
            filename: title + ".pdf",
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            pagebreak: { mode: ["css", "legacy"] },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
        };

        try {
            await window.html2pdf().set(opt).from(wrapper.querySelector("#__pdf_a4__")).save();
        } finally {
            document.body.removeChild(wrapper);
        }
    }

    function arrayBufferToBase64(buffer) {
        let binary = "";
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
    }

    const handleImportDocument = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".html,.htm,.txt,.docx,.pdf";
        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const nameNoExt = file.name.replace(/\.[^.]+$/, "");
            const ext = (file.name.split(".").pop() || "").toLowerCase();

            setSaving(false);
            setEditorId(null);
            setEditorTitulo(nameNoExt);
            setEditorDescripcion(`Importado desde ${file.name}`);
            setEditorCategoria("");
            setEditorScope("PRIVATE");
            setEditorEstado("BORRADOR");

            try {
                if (ext === "html" || ext === "htm") {
                    const reader = new FileReader();
                    reader.onload = () => {
                        setEditorHtml(String(reader.result || ""));
                        setEditorOpen(true);
                    };
                    reader.readAsText(file, "utf-8");
                    return;
                }

                if (ext === "txt") {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const raw = String(reader.result || "");
                        const paragraphs = raw
                            .split(/\r?\n\r?\n+/)
                            .map(p => p.trim())
                            .filter(Boolean)
                            .map(p => `<p>${p.replace(/\r?\n/g, "<br/>")}</p>`)
                            .join("");
                        setEditorHtml(paragraphs || "<p></p>");
                        setEditorOpen(true);
                    };
                    reader.readAsText(file, "utf-8");
                    return;
                }

                if (ext === "docx") {
                    await ensureMammoth();
                    const reader = new FileReader();
                    reader.onload = async () => {
                        try {
                            const { value: html } = await window.mammoth.convertToHtml({
                                arrayBuffer: reader.result
                            }, {
                                styleMap: [
                                    "p[style-name='Title'] => h1:fresh",
                                    "p[style-name='Heading 1'] => h1:fresh",
                                    "p[style-name='Heading 2'] => h2:fresh",
                                ]
                            });
                            const clean = html
                                .replace(/[\u2018\u2019]/g, "'")
                                .replace(/[\u201C\u201D]/g, '"')
                                .replace(/\u00A0/g, " ");
                            setEditorHtml(clean || "<p></p>");
                            setEditorOpen(true);
                        } catch (err) {
                            console.error("DOCX→HTML error:", err);
                            showSnack("No se pudo convertir el .docx", "error");
                        }
                    };
                    reader.readAsArrayBuffer(file);
                    return;
                }

                if (ext === "pdf") {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const b64 = arrayBufferToBase64(reader.result);
                        const embedded = `
            <p><em>Este contenido proviene de un PDF importado. No es editable.</em></p>
            <object data="data:application/pdf;base64,${b64}" type="application/pdf" width="100%" height="800">
              <embed src="data:application/pdf;base64,${b64}" type="application/pdf"/>
              <p>Tu navegador no puede mostrar el PDF embebido. <a download="${nameNoExt}.pdf" href="data:application/pdf;base64,${b64}">Descargar PDF</a></p>
            </object>
          `;
                        setEditorHtml(embedded);
                        setEditorOpen(true);
                    };
                    reader.readAsArrayBuffer(file);
                    return;
                }

                showSnack("Formato no soportado. Usá .html, .txt, .docx o .pdf.", "warning");
            } catch (err) {
                console.error("Import error:", err);
                showSnack("Error al importar el archivo.", "error");
            }
        };
        input.click();
    };

    const handleExportSelected = async (itemToExport = selected) => {
        if (!itemToExport) {
            showSnack("Seleccioná un documento para exportar", "warning");
            return;
        }

        try {
            setCargando(true);
            const data = await documentosGetJWT(itemToExport.id);
            const content = data?.item?.Contenido || "";

            const formato = await new Promise((resolve) => {
                const modal = document.createElement('div');
                modal.style.cssText = `
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background: white;
                padding: 24px;
                border-radius: 12px;
                box-shadow: 0 10px 40px rgba(0,0,0,0.2);
                z-index: 9999;
            `;

                modal.innerHTML = `
                <h3 style="margin: 0 0 16px 0; color: #2e7d6f;">Exportar Documento</h3>
                <p style="margin: 0 0 20px 0;">Seleccione el formato de exportación:</p>
                <div style="display: flex; gap: 12px;">
                    <button id="export-pdf" style="
                        padding: 12px 24px;
                        background: #2e7d6f;
                        color: white;
                        border: none;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 14px;
                    ">📄 PDF</button>
                    <button id="export-word" style="
                        padding: 12px 24px;
                        background: #2e7d6f;
                        color: white;
                        border: none;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 14px;
                    ">📝 Word</button>
                    <button id="export-html" style="
                        padding: 12px 24px;
                        background: #2e7d6f;
                        color: white;
                        border: none;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 14px;
                    ">🌐 HTML</button>
                    <button id="export-cancel" style="
                        padding: 12px 24px;
                        background: #ccc;
                        color: #333;
                        border: none;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 14px;
                    ">Cancelar</button>
                </div>
            `;

                document.body.appendChild(modal);

                document.getElementById('export-pdf').onclick = () => {
                    document.body.removeChild(modal);
                    resolve('pdf');
                };
                document.getElementById('export-word').onclick = () => {
                    document.body.removeChild(modal);
                    resolve('word');
                };
                document.getElementById('export-html').onclick = () => {
                    document.body.removeChild(modal);
                    resolve('html');
                };
                document.getElementById('export-cancel').onclick = () => {
                    document.body.removeChild(modal);
                    resolve(null);
                };
            });

            if (!formato) {
                setCargando(false);
                return;
            }

            const htmlContent = `<!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>${itemToExport.titulo}</title>
                <style>
                    @page { size: A4; margin: 2cm; }
                    body { 
                        font-family: 'Calibri', Arial, sans-serif; 
                        max-width: 800px; 
                        margin: 0 auto; 
                        padding: 20px;
                        line-height: 1.6;
                        color: #333;
                    }
                    h1 { color: #2e7d6f; border-bottom: 2px solid #2e7d6f; padding-bottom: 10px; }
                    h2 { color: #555; margin-top: 24px; }
                    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #2e7d6f; color: white; }
                    .metadata { 
                        background: #f5f5f5; 
                        padding: 10px; 
                        border-radius: 8px; 
                        margin-bottom: 20px;
                        font-size: 14px;
                    }
                </style>
            </head>
            <body>
                <h1>${itemToExport.titulo}</h1>
                <div class="metadata">
                    <strong>Autor:</strong> ${itemToExport.autor || 'Anónimo'}<br/>
                    <strong>Fecha:</strong> ${formatFecha(itemToExport.actualizado) || new Date().toLocaleDateString()}<br/>
                    ${itemToExport.categoria ? `<strong>Categoría:</strong> ${itemToExport.categoria}<br/>` : ''}
                    ${itemToExport.descripcion ? `<strong>Descripción:</strong> ${itemToExport.descripcion}` : ''}
                </div>
                <div class="content">
                    ${content}
                </div>
            </body>
            </html>`;

            if (formato === 'html') {
                const blob = new Blob([htmlContent], { type: 'text/html' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${itemToExport.titulo}.html`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

            } else if (formato === 'pdf') {
                const html = `
                <h1 style="margin:0 0 12px 0; color:#1f2937;">${itemToExport.titulo}</h1>
                ${itemToExport.descripcion ? `<p style="color:#475569">${itemToExport.descripcion}</p>` : ""}
                <hr style="border:none;border-top:1px solid #e5e7eb;margin:14px 0"/>
                ${content}
              `;
                await exportHTMLtoPDF({
                    title: itemToExport.titulo || "documento",
                    html: html,
                    marginMm: 18
                });
            } else if (formato === 'word') {
                const blob = new Blob([htmlContent], {
                    type: 'application/msword'
                });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${itemToExport.titulo}.doc`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }

        } catch (error) {
            console.error("Error exportando documento:", error);
            showSnack("Error al exportar el documento", "error");
        } finally {
            setCargando(false);
        }
    };

    const [statsOpen, setStatsOpen] = useState(false);
    // Categorías únicas para chips de filtro
    const catChips = useMemo(() => {
        const cats = {};
        items.forEach(item => {
            const cat = item.categoriaNombre || item.categoria || "Sin categoría";
            cats[cat] = (cats[cat] || 0) + 1;
        });
        return Object.entries(cats).sort((a, b) => {
            if (a[0] === "Sin categoría") return 1;
            if (b[0] === "Sin categoría") return -1;
            return a[0].localeCompare(b[0]);
        });
    }, [items]);

    // Items filtrados por categoría seleccionada
    const filteredByCat = useMemo(() => {
        if (catFilter === "todos") return items;
        return items.filter(item => {
            const cat = item.categoriaNombre || item.categoria || "Sin categoría";
            return cat === catFilter;
        });
    }, [items, catFilter]);

    const stats = useMemo(() => ({
        total: items.length,
        privados: items.filter(i => i.scope === "PRIVATE").length,
        globales: items.filter(i => i.scope === "GLOBAL").length,
        favoritos: items.filter(i => i.favorito).length,
        porAutor: (() => {
            const m = {};
            items.forEach(i => { const a = i.autor || 'Anónimo'; m[a] = (m[a] || 0) + 1; });
            return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 5);
        })()
    }), [items]);

    const handleShowDashboard = () => setStatsOpen(true);

    useEffect(() => {
        const onResize = () => setIsNarrow(window.innerWidth <= 1366);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (!selected || !selected.id) return;
        if (!showPreview) return;

        setPrevHtml("");
        handlePreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, showPreview]);

    const openPreviewFor = (item) => {
        if (item) setSelected(item);
        setShowPreview(true);
    };

    const closePreview = () => setShowPreview(false);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") closePreview(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => { localStorage.setItem("doc-leftWidth", String(leftWidth)); }, [leftWidth]);
    useEffect(() => { localStorage.setItem("doc-showPreview", showPreview ? "1" : "0"); }, [showPreview]);

    const [isResizing, setIsResizing] = useState(false);

    useEffect(() => {
        const onMove = (e) => {
            if (!isResizing) return;
            const rect = rootRef.current?.getBoundingClientRect();
            const offsetLeft = rect ? rect.left : 0;
            const next = Math.min(980, Math.max(420, e.clientX - offsetLeft));
            setLeftWidth(next);
        };
        const onUp = () => setIsResizing(false);

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [isResizing]);

    // ===== ATAJOS DE TECLADO =====
    useEffect(() => {
        const handleKeyboard = (e) => {
            const activeElement = document.activeElement;
            const isInputActive = activeElement && (
                activeElement.tagName === 'INPUT' ||
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.contentEditable === 'true'
            );

            if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
                e.preventDefault();
                const searchField = document.querySelector('input[placeholder*="Buscar"]');
                if (searchField) {
                    searchField.focus();
                    searchField.select();
                }
                return;
            }

            if (isInputActive || editorOpen || deleteConfirmOpen) return;

            if (e.altKey && e.key === 'n') {
                e.preventDefault();
                e.stopPropagation();
                openNuevo("PRIVATE");
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
                e.preventDefault();
                if (selected && puedeRealizarAccion(selected, 'edit')) {
                    openEditar(selected);
                }
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
                e.preventDefault();
                if (selected && puedeRealizarAccion(selected, 'duplicate')) {
                    duplicarComoPropio(selected);
                }
                return;
            }

            if (e.key === 'Delete' && !isInputActive) {
                if (selected && puedeRealizarAccion(selected, 'delete')) {
                    setItemToDelete(selected);
                    setDeleteConfirmOpen(true);
                }
                return;
            }

            if (e.key === 'Enter' && !isInputActive) {
                if (selected) {
                    openPreviewFor(selected);
                }
                return;
            }
        };

        window.addEventListener('keydown', handleKeyboard, true);
        return () => window.removeEventListener('keydown', handleKeyboard, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, editorOpen, deleteConfirmOpen, showPreview, puedeRealizarAccion]);

    const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

    const KeyboardHelpDialog = () => (
        <Dialog open={showKeyboardHelp} onClose={() => setShowKeyboardHelp(false)} maxWidth="sm">
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">Atajos de Teclado</Typography>
                    <IconButton onClick={() => setShowKeyboardHelp(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <List dense>
                    <ListItem>
                        <ListItemText
                            primary={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Chip label="Alt" size="small" style={{ fontSize: 11 }} />
                                    <Typography variant="body2">+</Typography>
                                    <Chip label="N" size="small" style={{ fontSize: 11 }} />
                                </Box>
                            }
                            secondary="Nuevo documento"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            primary={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Chip label="Ctrl" size="small" style={{ fontSize: 11 }} />
                                    <Typography variant="body2">+</Typography>
                                    <Chip label="E" size="small" style={{ fontSize: 11 }} />
                                </Box>
                            }
                            secondary="Editar documento seleccionado"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            primary={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Chip label="Ctrl" size="small" style={{ fontSize: 11 }} />
                                    <Typography variant="body2">+</Typography>
                                    <Chip label="D" size="small" style={{ fontSize: 11 }} />
                                </Box>
                            }
                            secondary="Duplicar documento seleccionado"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            primary={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Chip label="Ctrl" size="small" style={{ fontSize: 11 }} />
                                    <Typography variant="body2">+</Typography>
                                    <Chip label="F" size="small" style={{ fontSize: 11 }} />
                                </Box>
                            }
                            secondary="Buscar documentos"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            primary={<Chip label="Enter" size="small" style={{ fontSize: 11 }} />}
                            secondary="Previsualizar documento"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            primary={<Chip label="Delete" size="small" style={{ fontSize: 11 }} />}
                            secondary="Eliminar documento seleccionado"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            primary={<Chip label="Esc" size="small" style={{ fontSize: 11 }} />}
                            secondary="Cerrar vista previa"
                        />
                    </ListItem>
                </List>
                <Alert severity="info" style={{ marginTop: 16 }}>
                    <Typography variant="caption">
                        <strong>Nota:</strong> En Mac, usá Cmd en lugar de Ctrl.
                        Para nuevo documento usamos Alt+N porque Ctrl+N abre nueva ventana del navegador.
                    </Typography>
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowKeyboardHelp(false)} color="primary" variant="contained">
                    Entendido
                </Button>
            </DialogActions>
        </Dialog>
    );

    //const escritoNombre = useMemo(() => selected?.titulo || "", [selected]);

    // ===== GUARDADO AUTOMÁTICO =====
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(() =>
        localStorage.getItem('doc-autoSave') === 'true'
    );
    const [lastSaved, setLastSaved] = useState(null);
    const [unsavedChanges, setUnsavedChanges] = useState(false);

    // ===== FEEDBACK VISUAL AL ARRASTRAR =====
    const DragFeedback = () => {
        if (!isResizing) return null;

        return (
            <>
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.1)',
                    zIndex: 999,
                    pointerEvents: 'none'
                }} />

                <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: leftWidth + 8,
                    transform: 'translate(-50%, -50%)',
                    padding: '6px 12px',
                    background: '#2e7d6f',
                    color: 'white',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    zIndex: 1000,
                    pointerEvents: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                    {Math.round(leftWidth)}px
                </div>

                <div style={{
                    position: 'fixed',
                    top: 0,
                    bottom: 0,
                    left: leftWidth,
                    width: 2,
                    background: '#2e7d6f',
                    zIndex: 1000,
                    pointerEvents: 'none'
                }} />
            </>
        );
    };

    // ===== PLANTILLAS =====
    const [showTemplates, setShowTemplates] = useState(false);

    const templates = [
        {
            id: 'notificacion',
            titulo: 'Notificación Formal',
            categoria: 'Legal',
            descripcion: 'Plantilla para notificaciones oficiales',
            contenido: `
            <h1>NOTIFICACIÓN</h1>
            <p><strong>Ref:</strong> {REFERENCIA}</p>
            <p><strong>Fecha:</strong> {FECHA}</p>
            <br/>
            <p>Estimado/a {DESTINATARIO}:</p>
            <p>Por medio de la presente, se le notifica que {CONTENIDO_NOTIFICACION}</p>
            <p>Plazo para responder: {PLAZO} días hábiles desde la fecha de notificación.</p>
            <br/>
            <p>Atentamente,</p>
            <p>{FIRMANTE}<br/>{CARGO}</p>
        `
        },
        {
            id: 'resolucion',
            titulo: 'Resolución Administrativa',
            categoria: 'Legal',
            descripcion: 'Formato estándar de resolución',
            contenido: `
            <h1>RESOLUCIÓN Nº {NUMERO}</h1>
            <p><strong>VISTO:</strong> {EXPEDIENTE}</p>
            <p><strong>CONSIDERANDO:</strong></p>
            <p>{CONSIDERANDOS}</p>
            <p><strong>POR ELLO:</strong></p>
            <p><strong>SE RESUELVE:</strong></p>
            <p>Artículo 1º: {ARTICULO_1}</p>
            <p>Artículo 2º: De forma.</p>
        `
        },
        {
            id: 'informe',
            titulo: 'Informe Técnico',
            categoria: 'Reportes',
            descripcion: 'Estructura para informes técnicos',
            contenido: `
            <h1>INFORME TÉCNICO</h1>
            <h2>1. OBJETIVO</h2>
            <p>{OBJETIVO}</p>
            <h2>2. ANTECEDENTES</h2>
            <p>{ANTECEDENTES}</p>
            <h2>3. ANÁLISIS</h2>
            <p>{ANALISIS}</p>
            <h2>4. CONCLUSIONES</h2>
            <p>{CONCLUSIONES}</p>
            <h2>5. RECOMENDACIONES</h2>
            <p>{RECOMENDACIONES}</p>
        `
        }
    ];

    const TemplatesDialog = () => (
        <Dialog open={showTemplates} onClose={() => setShowTemplates(false)} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">Seleccionar Plantilla</Typography>
                    <IconButton onClick={() => setShowTemplates(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    {templates.map(template => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
                            <Card
                                style={{
                                    cursor: 'pointer',
                                    height: '100%',
                                    transition: 'all 0.2s',
                                    border: '2px solid transparent'
                                }}
                                onClick={() => {
                                    setEditorId(null);
                                    setEditorTitulo(template.titulo);
                                    setEditorDescripcion(template.descripcion);
                                    setEditorCategoria(template.categoria);
                                    setEditorHtml(template.contenido);
                                    setEditorScope("PRIVATE");
                                    setEditorEstado("BORRADOR");
                                    setShowTemplates(false);
                                    setEditorOpen(true);
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.border = '2px solid #2e7d6f';
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.border = '2px solid transparent';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <CardContent>
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <DescriptionIcon color="primary" />
                                        <Typography variant="h6">{template.titulo}</Typography>
                                    </Box>
                                    <Chip
                                        label={template.categoria}
                                        size="small"
                                        variant="outlined"
                                        style={{ marginBottom: 8 }}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                        {template.descripcion}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
        </Dialog>
    );

    // ===== CONFIRMACIÓN AL SALIR CON CAMBIOS =====
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (editorOpen && unsavedChanges) {
                e.preventDefault();
                e.returnValue = 'Tienes cambios sin guardar. ¿Estás seguro de que quieres salir?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [editorOpen, unsavedChanges]);

    // Cargar lista
    const loadList = useCallback(async () => {
        setCargando(true);
        try {
            const mapItems = (raw) => {
                const arr = raw?.items || [];
                return arr.map(it => {
                    const estado = (it.Estado ?? it.estado ?? "").toUpperCase() || "BORRADOR";
                    const activo = typeof it.Activo === "boolean"
                        ? it.Activo
                        : (estado !== "INACTIVO");

                    return {
                        id: it.Id ?? it.id,
                        titulo: it.Escrito ?? it.escrito ?? it.titulo,
                        descripcion: it.Description ?? it.Descripcion ?? it.descripcion ?? "",
                        scope: it.Scope ?? it.scope ?? "GLOBAL",
                        actualizado: it.FechaUltMod ?? it.actualizado ?? "",
                        autor: (it.AutorNombre ?? it.OwnerUsuario ??
                            it.AutorNombre ?? it.Autor ?? it.UsuarioNombre ?? it.Usuario ??
                            it.CreadoPorNombre ?? it.CreadoPor ?? it.OwnerNombre ?? it.Owner ??
                            it.UsuarioCreador ?? it.UsuarioCreadorNombre ?? ""),
                        OwnerUsuario: it.OwnerUsuario ?? it.ownerUsuario ?? it.Usuario ?? it.UsuarioCreador ?? it.UsuarioNombre ?? it.Owner ?? "",
                        compartidoCon: it.CompartidoCon ?? it.compartidoCon ?? [],
                        sharedWithMe: !!(it.SharedWithMe ?? it.sharedWithMe),
                        favorito: Boolean(it.EsFavorito) || favorites.includes(it.Id ?? it.id),
                        usado: it.VecesUsado ?? 0,
                        estado,
                        activo,
                        categoria: it.Categoria ?? "",
                        categoriaNombre: it.Categoria_Nombre ?? it.categoriaNombre ?? it.Categoria ?? "",
                        idCategoria: it.Id_Categoria ?? it.idCategoria ?? null,
                    };
                }).filter(x => x.id && x.titulo);
            };

            const res = await documentosListarJWT(
                TABS[tab].key,
                qDeb || "",
                advancedFilters.categoria || "",
                null
            );
            const m = mapItems(res);

            let filtered = m;
            if (advancedFilters.categoria) {
                filtered = filtered.filter(item =>
                    (item.categoria ?? "").toLowerCase().includes(advancedFilters.categoria.toLowerCase())
                );
            }
            if (advancedFilters.autor) {
                filtered = filtered.filter(item =>
                    (item.autor ?? "").toLowerCase().includes(advancedFilters.autor.toLowerCase())
                );
            }

            setItems(filtered);

            const t = TABS[tab]?.key;
            if (t === "todos") {
                filtered = filtered.filter(i => i.scope === "GLOBAL" && i.estado === "PUBLICADO" && i.activo);
            } else if (t === "mios") {
                filtered = filtered.filter(i => i.scope === "PRIVATE" && i.estado === "PUBLICADO" && i.activo);
            } else if (t === "compartidos") {
                filtered = filtered.filter(i =>
                    i.sharedWithMe && i.estado === "PUBLICADO" && i.activo
                );
            } else if (t === "favoritos") {
                filtered = filtered.filter(i => i.favorito && i.estado === "PUBLICADO" && i.activo);
            } else if (t === "borradores") {
                filtered = filtered.filter(i => i.estado === "BORRADOR");
            } else if (t === "archivados") {
                filtered = filtered.filter(i => !i.activo);
            }

            if (filtered.length === 1 && filtered[0]?.id) {
                setSelected(filtered[0]);
            }
        } catch {
            setItems([]);
        } finally {
            setCargando(false);
        }
    }, [tab, qDeb, favorites, advancedFilters, TABS]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadList(); }, [loadList]);

    // Cargar variables globales (una sola vez)
    useEffect(() => {
        (async () => {
            try {
                const res = await documentosVarsJWT();
                // Ahora las variables vienen como objetos con {key, desc, categoria}
                const v = (res?.vars || []).map(x => {
                    if (typeof x === "string") return { key: x, desc: null, categoria: 'Otras' };
                    return {
                        key: x.key || x.nombre,
                        desc: x.desc || x.descripcion,
                        categoria: x.categoria || 'Otras'
                    };
                }).filter(x => x.key);
                setVars(v);
            } catch {
                setVars([]);
            }
        })();
    }, []);

    // Preview del escrito
    const handlePreview = async () => {
        if (!selected) {
            setPrevHtml("<div style='padding:20px;text-align:center;color:#888'>Seleccioná un escrito para previsualizar</div>");
            return;
        }

        setPrevLoading(true);

        try {
            // Si hay expediente, usar preview con variables reemplazadas
            if (expediente?.trim()) {
                const data = await documentosPreviewJWT({
                    id: selected.id,
                    expedienteId: expediente.trim(),
                });

                if (data?.ok) {
                    setPrevHtml(data.html || "");
                } else {
                    setPrevHtml(`<div style='padding:20px;color:#c62828'>${data?.mensaje || "Error al previsualizar"}</div>`);
                }
            } else {
                // Si NO hay expediente, obtener el HTML directamente sin reemplazar variables
                const data = await documentosGetJWT(selected.id);
                const content = data?.item?.Contenido || data?.item?.contenido || "";

                if (content) {
                    setPrevHtml(content);
                } else {
                    setPrevHtml("<div style='padding:20px;color:#c62828'>No se pudo cargar el contenido del escrito</div>");
                }
            }
        } catch {
            setPrevHtml("<div style='padding:20px;color:#c62828'>Error cargando previsualización</div>");
        } finally {
            setPrevLoading(false);
        }
    };

    // Abrir editor para nuevo documento
    const openNuevo = (scope = "PRIVATE") => {
        setSaving(false);
        setEditorId(null);
        setEditorTitulo("");
        setEditorDescripcion("");
        setEditorCategoria("");
        setEditorScope(scope);
        setEditorHtml("<p>Escribí tu documento aquí...</p>");
        setEditorEstado("BORRADOR");
        setEditorOpen(true);
        setEditorActivo(true);
    };

    // Abrir editor para editar
    const openEditar = async (it) => {
        setCargando(true);
        try {
            const data = await documentosGetJWT(it.id);
            const row = data?.item || {};
            setSaving(false);
            setEditorId(row.Id || it.id);
            setEditorTitulo(row.Escrito || it.titulo || "");
            setEditorDescripcion(row.Description || row.Descripcion || "");

            // 🔹 Texto de categoría (por compatibilidad con código viejo)
            setEditorCategoria(row.Categoria || "");

            // 🔹 Id de categoría (para el Autocomplete)
            setEditorCategoriaId(
                row.Id_Categoria !== undefined && row.Id_Categoria !== null
                    ? row.Id_Categoria
                    : null
            );

            setEditorScope(row.Scope || "PRIVATE");
            setEditorHtml(row.Contenido || "");
            setEditorEstado(row.Estado || "BORRADOR");
            setEditorOpen(true);
            setEditorActivo(
                typeof row.Activo === "boolean"
                    ? row.Activo
                    : ((row.Estado ?? "").toUpperCase() !== "INACTIVO")
            );
        } catch {
            showSnack("No se pudo cargar el escrito", "error");
        } finally {
            setCargando(false);
        }
    };


    // Duplicar documento
    const duplicarComoPropio = async (it) => {
        setCargando(true);
        try {
            const data = await documentosGetJWT(it.id);
            const row = data?.item || {};
            setSaving(false);
            setEditorId(null);
            setEditorTitulo(`${row.Escrito || it.titulo || "Escrito"} (Copia)`);
            setEditorDescripcion(row.Description || "");

            setEditorCategoria(row.Categoria || "");
            setEditorCategoriaId(
                row.Id_Categoria !== undefined && row.Id_Categoria !== null
                    ? row.Id_Categoria
                    : null
            );

            setEditorScope("PRIVATE");
            setEditorHtml(row.Contenido || "");
            setEditorEstado("BORRADOR");
            setEditorOpen(true);
            setEditorActivo(true);
        } catch {
            showSnack("No se pudo duplicar el documento", "error");
        } finally {
            setCargando(false);
        }
    };


    // Guardar documento
    const guardarEscrito = useCallback(async () => {
        if (!editorTitulo.trim()) {
            showSnack("El título es obligatorio", "warning");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                titulo: editorTitulo.trim(),
                escrito: editorTitulo.trim(),
                descripcion: (editorDescripcion || "").trim(),
                categoria: (editorCategoria || "").trim(),
                idCategoria: editorCategoriaId || null,
                contenido: editorApiRef.current?.getContent?.() ?? editorHtml ?? "",
                scope: editorScope,
                activo: !!editorActivo,
                estado: editorEstado
            };

            if (editorId && Number(editorId) > 0) {
                payload.id = Number(editorId);
            }

            const res = await documentosSaveJWT(payload);

            if (res?.ok) {
                setEditorOpen(false);
                setUnsavedChanges(false);
                await loadList();
                return true;
            } else {
                showSnack(res?.mensaje || "Error al guardar", "error");
                return false;
            }
        } catch (err) {
            console.error(err);
            showSnack(err?.message || "Error de red al guardar", "error");
            return false;
        } finally {
            setSaving(false);
        }
    }, [editorTitulo, editorDescripcion, editorCategoria, editorCategoriaId, editorHtml, editorScope, editorEstado, editorId, editorActivo, loadList]);

    useEffect(() => {
        if (!autoSaveEnabled || !editorOpen || !editorTitulo || saving || !unsavedChanges) return;

        const saveTimer = setTimeout(async () => {
            const success = await guardarEscrito();
            if (success) {
                setLastSaved(new Date());
                setUnsavedChanges(false);
            }
        }, 30000);

        return () => clearTimeout(saveTimer);
    }, [autoSaveEnabled, editorOpen, editorTitulo, unsavedChanges, saving, guardarEscrito]);

    // Eliminar documento
    const handleDelete = async () => {
        if (!itemToDelete) return;

        try {
            const res = await documentosDeleteJWT(itemToDelete.id);

            if (res?.ok) {
                setDeleteConfirmOpen(false);
                setItemToDelete(null);
                await loadList();
                if (selected?.id === itemToDelete.id) {
                    setSelected(null);
                    setPrevHtml("");
                }
            } else {
                showSnack(res?.mensaje || "Error al eliminar", "error");
            }
        } catch {
            showSnack("Error de red al eliminar", "error");
        }
    };

    // Toggle favorito
    const toggleFavorite = async (item) => {
        try {
            await documentosFavoritoJWT(item.id, !item.favorito);
            setItems(prev => prev.map(x =>
                x.id === item.id ? { ...x, favorito: !item.favorito } : x
            ));
            if (TABS[tab].key === "favoritos") loadList();
        } catch (e) {
            showSnack("No se pudo actualizar favoritos", "error");
        }
    };

    // ===== DESCOMPARTIR CONMIGO =====
    const handleUnshareMe = async () => {
        if (!itemToUnshare) return;

        try {
            const res = await documentosUnshareJWT(itemToUnshare.id);

            if (res?.ok) {
                setUnshareConfirmOpen(false);
                setItemToUnshare(null);
                await loadList();
                if (selected?.id === itemToUnshare.id) {
                    setSelected(null);
                    setPrevHtml("");
                }
            } else {
                showSnack(res?.mensaje || "Error al descompartir", "error");
            }
        } catch {
            showSnack("Error de red al descompartir", "error");
        }
    };

    // Menú contextual
    const handleContextMenu = (event, item) => {
        event.preventDefault();
        setContextMenu({ x: event.clientX - 2, y: event.clientY - 4 });
        setContextItem(item);
    };

    const closeContextMenu = () => {
        setContextMenu(null);
        setContextItem(null);
    };

    return (
        <>
        <style>{`
            @keyframes docSlideIn {
                from { transform: translateX(12px); opacity: 0.001; }
                to   { transform: translateX(0);    opacity: 1; }
            }
            @keyframes docFadeIn {
                from { opacity: 0; }
                to   { opacity: 1; }
            }
        `}</style>
        <Box
            ref={rootRef}
            sx={{
                ...styles.root,
                gridTemplateColumns: (!isNarrow && showPreview)
                    ? `${Math.max(420, Math.min(leftWidth, 980))}px 8px minmax(420px, 1fr)`
                    : "1fr",
                userSelect: isResizing ? "none" : "auto"
            }}
        >
            {/* ========== PANEL IZQUIERDO: LISTA ========== */}
            <Paper sx={styles.paperPanel} elevation={3}>
                {/* Header */}
                <Box sx={styles.panelHeader}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <div>
                            <Typography variant="h6" sx={styles.panelTitleFix}>
                                Gestor de Documentos
                            </Typography>
                            <Typography variant="body2" sx={styles.panelSubtitleFix}>
                                {items.length} documentos disponibles
                            </Typography>
                        </div>

                        <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title="Crear Nuevo Documento (Alt+N)">
                                <Button
                                    onClick={() => openNuevo("PRIVATE")}
                                    sx={styles.ghostButton}
                                    startIcon={<Box sx={styles.plusBadge}><AddIcon fontSize="small" /></Box>}
                                    size={isNarrow ? "small" : "medium"}
                                >
                                    Nuevo Documento
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>

                {/* Tabs */}
                <Tabs
                    value={tab}
                    onChange={(e, v) => {
                        setTab(v);
                        setSelected(null);
                        setPrevHtml("");
                    }}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    sx={styles.tabsContainer}
                >
                    {TABS.map((t, idx) => (
                        <Tab
                            key={t.key}
                            label={
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    {t.icon}
                                    <span>{t.label}</span>
                                </Box>
                            }
                        />
                    ))}
                </Tabs>

                {/* Toolbar de búsqueda y filtros */}
                <Box sx={styles.listToolbar}>
                    <TextField
                        sx={styles.searchField}
                        variant="outlined"
                        size="small"
                        placeholder="Buscar documentos..."
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: q && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setQ('')}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <div style={{ flex: 1 }} />

                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newView) => {
                            if (newView !== null) {
                                setViewMode(newView);
                            }
                        }}
                        size="small"
                        sx={styles.viewToggle}
                    >
                        <Tooltip title="Ver Lista">
                            <ToggleButton value="list" aria-label="vista lista">
                                <ViewListIcon />
                            </ToggleButton>
                        </Tooltip>

                        <Tooltip title="Ver Grilla">
                            <ToggleButton value="grid" aria-label="vista grid">
                                <ViewModuleIcon />
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>

                    <Tooltip title="Estadisticas">
                        <IconButton
                            onClick={() => { setSpeedDialOpen(false); handleShowDashboard(); }}
                            size="large">
                            <AssessmentIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Filtros">
                        <IconButton
                            onClick={() => setShowFilters(!showFilters)}
                            color={showFilters ? "primary" : "default"}
                            size="large">
                            <Badge badgeContent={modulo !== "todos" ? 1 : 0} color="secondary">
                                <FilterListIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Actualizar">
                        <span>
                            <IconButton onClick={loadList} disabled={cargando} size="large">
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="Atajos de teclado (?)">
                        <IconButton
                            onClick={() => setShowKeyboardHelp(true)}
                            size="small"
                        >
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Filtros expandibles */}
                <Collapse in={showFilters}>
                    <Box p={2} bgcolor="#f8f9fa" borderBottom="1px solid #e0e0e0">
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Módulo</InputLabel>
                            <Select
                                label="Módulo"
                                value={modulo}
                                onChange={e => setModulo(e.target.value)}
                            >
                                <MenuItem value="todos">Todos los módulos</MenuItem>
                                <MenuItem value="TRIBUTARIAS">Ejec. Tributarias</MenuItem>
                                <MenuItem value="NO_TRIBUTARIAS">Ejec. No Tributarias</MenuItem>
                                <MenuItem value="CONCURSOS">Concursos</MenuItem>
                                <MenuItem value="CONTENCIOSO">Contencioso</MenuItem>
                                <MenuItem value="DAP">DAP</MenuItem>
                                <MenuItem value="INMUEBLES">Inmuebles</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Collapse>

                {/* Chips de categoría */}
                {items.length > 0 && catChips.length > 1 && (
                    <Box px={2} py={1} display="flex" gap={0.75} flexWrap="wrap" borderBottom="1px solid #e0e0e0" bgcolor="#fafafa">
                        <Chip
                            label={`Todos (${items.length})`}
                            size="small"
                            variant={catFilter === "todos" ? "filled" : "outlined"}
                            color={catFilter === "todos" ? "primary" : "default"}
                            onClick={() => setCatFilter("todos")}
                            style={{ fontWeight: catFilter === "todos" ? 700 : 400 }}
                        />
                        {catChips.map(([cat, count]) => (
                            <Chip
                                key={cat}
                                label={`${cat} (${count})`}
                                size="small"
                                variant={catFilter === cat ? "filled" : "outlined"}
                                color={catFilter === cat ? "primary" : "default"}
                                onClick={() => setCatFilter(cat)}
                                style={{ fontWeight: catFilter === cat ? 700 : 400 }}
                            />
                        ))}
                    </Box>
                )}

                {/* Lista de documentos */}
                <Box sx={styles.listBox}>
                    {viewMode === 'list' ? (
                        cargando ? (
                            <Box p={3}>
                                {[1, 2, 3].map(i => (
                                    <Skeleton key={i} variant="rectangular" height={70} style={{ marginBottom: 8, borderRadius: 4 }} />
                                ))}
                            </Box>
                        ) : items.length === 0 ? (
                            <Box sx={styles.emptyState}>
                                <DescriptionIcon />
                                <Typography variant="h6">Sin documentos</Typography>
                                <Typography variant="body2">
                                    No hay documentos en esta vista
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {filteredByCat.map(it => (
                                    <ListItem
                                        key={it.id}
                                        button
                                        sx={styles.listItem}
                                        selected={selected?.id === it.id}
                                        onClick={() => setSelected(it)}
                                        onDoubleClick={() => openPreviewFor(it)}
                                        onKeyDown={(e) => { if (e.key === "Enter") openPreviewFor(it); }}
                                        title="Doble clic para previsualizar (Enter)"
                                        onContextMenu={(e) => handleContextMenu(e, it)}
                                    >
                                        <ListItemAvatar>
                                            <Box sx={styles.itemIcon}>
                                                <DescriptionIcon />
                                            </Box>
                                        </ListItemAvatar>

                                        <ListItemText
                                            secondaryTypographyProps={{ component: 'div' }}
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                                        {it.titulo}
                                                    </Typography>
                                                    {it.favorito && <StarIcon style={{ fontSize: 16, color: "#ffd700" }} />}
                                                    {it.scope === "GLOBAL" ? (
                                                        <Chip
                                                            icon={<PublicIcon style={{ fontSize: 14 }} />}
                                                            label="Público"
                                                            size="small"
                                                            sx={styles.itemBadge}
                                                            style={{ background: "#e3f2fd", color: "#1976d2" }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<LockIcon style={{ fontSize: 14 }} />}
                                                            label="Privado"
                                                            size="small"
                                                            sx={styles.itemBadge}
                                                            style={{ background: "#fff3e0", color: "#f57c00" }}
                                                        />
                                                    )}
                                                    {it.estado === "BORRADOR" && (
                                                        <Chip
                                                            label="Borrador"
                                                            size="small"
                                                            sx={styles.itemBadge}
                                                            style={{ background: "#e8eaf6", color: "#3f51b5" }}
                                                        />
                                                    )}
                                                    {(it.SharedWithMe || TABS[tab].key === "compartidos") && (
                                                        <Chip
                                                            icon={<ShareIcon style={{ fontSize: 14 }} />}
                                                            label="Compartido"
                                                            size="small"
                                                            sx={styles.itemBadge}
                                                            style={{ background: "#e8f5e9", color: "#2e7d32" }}
                                                        />
                                                    )}
                                                    {!it.activo && (
                                                        <Chip label="Inactivo" size="small"
                                                            sx={styles.itemBadge}
                                                            style={{ background: "#ffebee", color: "#c62828" }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box display="flex" flexDirection="column" mt={0.5}>
                                                    {it.descripcion && (
                                                        <Typography variant="body2" color="text.secondary" noWrap title={it.descripcion}>
                                                            {it.descripcion}
                                                        </Typography>
                                                    )}
                                                    <Box display="flex" alignItems="center" mt={it.descripcion ? 0.5 : 0}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Por {it.autor}
                                                        </Typography>
                                                        {it.actualizado && (
                                                            <>
                                                                <Typography variant="caption" color="text.secondary" style={{ opacity: 0.5, padding: '0 5px' }}>
                                                                    •
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatFecha(it.actualizado)}
                                                                </Typography>
                                                            </>
                                                        )}
                                                        {it.usado > 0 && (
                                                            <>
                                                                <Typography variant="caption" color="text.secondary" style={{ opacity: 0.5, padding: '0 5px' }}>
                                                                    •
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Usado {it.usado} veces
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Box>
                                            }
                                        />

                                        <ListItemSecondaryAction>
                                            {puedeRealizarAccion(it, 'favorite') && (
                                                <Tooltip title={it.favorito ? "Quitar de favoritos" : "Agregar a favoritos"}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleFavorite(it);
                                                        }}
                                                    >
                                                        {it.favorito ? <StarIcon style={{ color: "#ffd700" }} /> : <StarBorderIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {puedeRealizarAccion(it, 'share') && (
                                                <Tooltip title="Compartir">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openShareDialog(it);
                                                        }}
                                                    >
                                                        <ShareIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {puedeRealizarAccion(it, 'edit') && (
                                                <Tooltip title="Editar (Ctrl+E)">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openEditar(it);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {puedeRealizarAccion(it, 'duplicate') && (
                                                <Tooltip title="Duplicar (Ctrl+D)">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            duplicarComoPropio(it);
                                                        }}
                                                    >
                                                        <FileCopyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {puedeRealizarAccion(it, 'delete') && (
                                                <Tooltip title="Eliminar (Del)">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setItemToDelete(it);
                                                            setDeleteConfirmOpen(true);
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {puedeRealizarAccion(it, 'unshare-self') && (
                                                <Tooltip title="Dejar de compartir conmigo">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setItemToUnshare(it);
                                                            setUnshareConfirmOpen(true);
                                                        }}
                                                    >
                                                        <ExitToAppIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        )
                    ) : (
                        // VISTA GRID
                        (<Grid container spacing={2} sx={styles.gridContainer}>
                            {cargando ? (
                                [1, 2, 3, 4, 5, 6].map(i => (
                                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
                                        <Skeleton variant="rectangular" height={220} style={{ borderRadius: 4 }} />
                                    </Grid>
                                ))
                            ) : items.length === 0 ? (
                                <Grid size={12}>
                                    <Box sx={styles.emptyState}>
                                        <DescriptionIcon />
                                        <Typography variant="h6">Sin documentos</Typography>
                                        <Typography variant="body2">
                                            No hay documentos en esta vista
                                        </Typography>
                                    </Box>
                                </Grid>
                            ) : (
                                filteredByCat.map(item => (
                                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={item.id}>
                                        <Card
                                            sx={{ ...styles.documentCard, ...(selected?.id === item.id ? styles.documentCardSelected : {}) }}
                                            elevation={selected?.id === item.id ? 8 : 2}
                                        >
                                            <CardActionArea
                                                onClick={() => setSelected(item)}
                                                onDoubleClick={() => openPreviewFor(item)}
                                            >
                                                <CardHeader
                                                    avatar={
                                                        <Avatar sx={styles.cardAvatar}>
                                                            <DescriptionIcon />
                                                        </Avatar>
                                                    }
                                                    action={
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleContextMenu(e, item);
                                                            }}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    }
                                                    title={
                                                        <Tooltip title={item.titulo}>
                                                            <Typography
                                                                variant="subtitle1"
                                                                style={{
                                                                    fontWeight: 600,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    lineHeight: 1.2
                                                                }}
                                                            >
                                                                {item.titulo}
                                                            </Typography>
                                                        </Tooltip>
                                                    }
                                                    subheader={
                                                        item.autor
                                                            ? <Typography variant="caption" color="text.secondary">{item.autor}</Typography>
                                                            : null
                                                    }
                                                />
                                                <CardContent style={{ paddingTop: 0 }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        style={{
                                                            height: 40,
                                                            overflow: 'hidden',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical'
                                                        }}
                                                    >
                                                        {item.descripcion || 'Sin descripción'}
                                                    </Typography>
                                                    <Box mt={2} display="flex" gap={0.5} flexWrap="wrap">
                                                        {item.scope === "GLOBAL" ? (
                                                            <Chip
                                                                icon={<PublicIcon style={{ fontSize: 14 }} />}
                                                                label="Público"
                                                                size="small"
                                                                style={{ background: "#e3f2fd", color: "#1976d2" }}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                icon={<LockIcon style={{ fontSize: 14 }} />}
                                                                label="Privado"
                                                                size="small"
                                                                style={{ background: "#fff3e0", color: "#f57c00" }}
                                                            />
                                                        )}
                                                        {item.favorito && (
                                                            <Chip
                                                                icon={<StarIcon style={{ fontSize: 14, color: "#ffd700" }} />}
                                                                label="Favorito"
                                                                size="small"
                                                                variant="outlined"
                                                                style={{ borderColor: "#ffd700" }}
                                                            />
                                                        )}
                                                        {(item.SharedWithMe || TABS[tab].key === "compartidos") && (
                                                            <Chip
                                                                icon={<ShareIcon style={{ fontSize: 14 }} />}
                                                                label="Compartido"
                                                                size="small"
                                                                style={{ background: "#e8f5e9", color: "#2e7d32" }}
                                                            />
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </CardActionArea>

                                            <CardActions style={{ justifyContent: 'space-between' }}>
                                                <Box display="flex" gap={0.5}>
                                                    {puedeRealizarAccion(item, 'favorite') && (
                                                        <Tooltip title={item.favorito ? "Quitar de favoritos" : "Agregar a favoritos"}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleFavorite(item);
                                                                }}
                                                            >
                                                                {item.favorito ?
                                                                    <StarIcon style={{ color: "#ffd700", fontSize: 20 }} /> :
                                                                    <StarBorderIcon style={{ fontSize: 20 }} />
                                                                }
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {puedeRealizarAccion(item, 'share') && (
                                                        <Tooltip title="Compartir">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openShareDialog(item);
                                                                }}
                                                            >
                                                                <ShareIcon style={{ fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {puedeRealizarAccion(item, 'edit') && (
                                                        <Tooltip title="Editar">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openEditar(item);
                                                                }}
                                                            >
                                                                <EditIcon style={{ fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {puedeRealizarAccion(item, 'duplicate') && (
                                                        <Tooltip title="Duplicar">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    duplicarComoPropio(item);
                                                                }}
                                                            >
                                                                <FileCopyIcon style={{ fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {puedeRealizarAccion(item, 'delete') && (
                                                        <Tooltip title="Eliminar">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setItemToDelete(item);
                                                                    setDeleteConfirmOpen(true);
                                                                }}
                                                            >
                                                                <DeleteOutlineIcon color="error" style={{ fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {puedeRealizarAccion(item, 'unshare-self') && (
                                                        <Tooltip title="Dejar de compartir conmigo">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setItemToUnshare(item);
                                                                    setUnshareConfirmOpen(true);
                                                                }}
                                                            >
                                                                <ExitToAppIcon style={{ fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatFecha(item.actualizado)}
                                                </Typography>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))
                            )}
                        </Grid>)
                    )}
                </Box>
            </Paper>
            {/* Resizer */}
            {!isNarrow && showPreview && (
                <Box
                    sx={styles.resizer}
                    onMouseDown={() => setIsResizing(true)}
                    title="Arrastrá para redimensionar"
                />
            )}
            {/* Panel derecho: PREVIEW */}
            {!isNarrow && showPreview && (
                <Paper sx={styles.previewPanel} elevation={3}>
                    <Box sx={styles.previewHeader}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" sx={styles.panelTitleFix}>
                                {selected ? selected.titulo : "Vista Previa"}
                            </Typography>
                            <Box display="flex" gap={1}>
                                {selected && prevHtml && (
                                    <>
                                        <Tooltip title="Imprimir">
                                            <IconButton
                                                sx={styles.closeGhost}
                                                size="small"
                                                onClick={() => {
                                                    const printWindow = window.open('', '_blank');
                                                    printWindow.document.write(`
                                                        <!DOCTYPE html>
                                                        <html>
                                                        <head>
                                                            <title>${selected.titulo}</title>
                                                            <style>
                                                                @media print {
                                                                    body { margin: 0; }
                                                                }
                                                                body {
                                                                    font-family: 'Times New Roman', Times, serif;
                                                                    padding: 20mm;
                                                                    line-height: 1.6;
                                                                }
                                                            </style>
                                                        </head>
                                                        <body>${prevHtml}</body>
                                                        </html>
                                                    `);
                                                    printWindow.document.close();
                                                    setTimeout(() => {
                                                        printWindow.print();
                                                        printWindow.close();
                                                    }, 250);
                                                }}
                                            >
                                                <PrintIcon />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Descargar PDF">
                                            <IconButton
                                                sx={styles.closeGhost}
                                                size="small"
                                                onClick={async () => {
                                                    await exportHTMLtoPDF({
                                                        title: selected.titulo || "documento",
                                                        html: prevHtml,
                                                        marginMm: 18
                                                    });
                                                }}
                                            >
                                                <PictureAsPdfIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                                <IconButton
                                    sx={styles.closeGhost}
                                    onClick={closePreview}
                                    aria-label="Cerrar vista previa"
                                    size="large">
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>

                    {selected && (
                        <Collapse in={showVarsPreview && vars.length > 0}>
                            <Box sx={styles.varsBox}>
                                <Typography variant="caption" style={{ marginRight: 8, fontWeight: 600 }}>
                                    Variables:
                                </Typography>
                                {vars.map(v => (
                                    <Chip
                                        key={v}
                                        size="small"
                                        label={`{${v}}`}
                                        sx={styles.varChip}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Collapse>
                    )}

                    <Box sx={styles.previewBox}>
                        {prevLoading && (
                            <Box sx={styles.loadingOverlay}>
                                <CircularProgress size={60} />
                            </Box>
                        )}

                        {!selected ? (
                            <Box sx={styles.emptyState}>
                                <VisibilityIcon />
                                <Typography variant="h6">Sin selección</Typography>
                                <Typography variant="body2">
                                    Seleccioná un documento de la lista para previsualizar
                                </Typography>
                            </Box>
                        ) : prevHtml ? (
                            <Box sx={styles.a4Page}>
                                <Box sx={styles.a4Inner} dangerouslySetInnerHTML={{ __html: prevHtml }} />
                            </Box>
                        ) : (
                            <Box sx={styles.emptyState}>
                                <Typography variant="body1" color="text.secondary">
                                    Hacé clic en "Previsualizar" para ver el documento
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            )}
            {/* Overlay de preview (pantallas chicas) */}
            {isNarrow && showPreview && (
                <>
                    <Box
                        sx={styles.backdrop}
                        onClick={() => setShowPreview(false)}
                    />

                    <Paper sx={styles.previewOverlay}>
                        <Box sx={styles.previewHeader}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6" sx={styles.panelTitleFix}>
                                    {selected ? selected.titulo : "Vista Previa"}
                                </Typography>
                                <Box display="flex" gap={1}>
                                    {selected && prevHtml && (
                                        <>
                                            <Tooltip title="Imprimir">
                                                <IconButton
                                                    sx={styles.closeGhost}
                                                    size="small"
                                                    onClick={() => {
                                                        const printWindow = window.open('', '_blank');
                                                        printWindow.document.write(`
                                                            <!DOCTYPE html>
                                                            <html>
                                                            <head>
                                                                <title>${selected.titulo}</title>
                                                                <style>
                                                                    @media print {
                                                                        body { margin: 0; }
                                                                    }
                                                                    body {
                                                                        font-family: 'Times New Roman', Times, serif;
                                                                        padding: 20mm;
                                                                        line-height: 1.6;
                                                                    }
                                                                </style>
                                                            </head>
                                                            <body>${prevHtml}</body>
                                                            </html>
                                                        `);
                                                        printWindow.document.close();
                                                        setTimeout(() => {
                                                            printWindow.print();
                                                            printWindow.close();
                                                        }, 250);
                                                    }}
                                                >
                                                    <PrintIcon />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Descargar PDF">
                                                <IconButton
                                                    sx={styles.closeGhost}
                                                    size="small"
                                                    onClick={async () => {
                                                        await exportHTMLtoPDF({
                                                            title: selected.titulo || "documento",
                                                            html: prevHtml,
                                                            marginMm: 18
                                                        });
                                                    }}
                                                >
                                                    <PictureAsPdfIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                    <IconButton
                                        sx={styles.closeGhost}
                                        onClick={() => setShowPreview(false)}
                                        size="large">
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={styles.previewBox}>
                            {prevLoading && (
                                <Box sx={styles.loadingOverlay}>
                                    <CircularProgress size={60} />
                                </Box>
                            )}

                            {!selected ? (
                                <Box sx={styles.emptyState}>
                                    <VisibilityIcon />
                                    <Typography variant="h6">Sin selección</Typography>
                                    <Typography variant="body2">Seleccioná un documento</Typography>
                                </Box>
                            ) : !prevHtml ? (
                                <Box sx={styles.emptyState}>
                                    <CircularProgress />
                                    <Typography variant="body1" color="text.secondary" style={{ marginTop: 16 }}>
                                        Cargando vista previa...
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={styles.a4Page}>
                                    <Box sx={styles.a4Inner} dangerouslySetInnerHTML={{ __html: prevHtml }} />
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </>
            )}
            {/* Diálogo de estadísticas */}
            <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <AssessmentIcon color="primary" />
                        Estadísticas
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {[
                            { label: 'Total', value: stats.total },
                            { label: 'Privados', value: stats.privados },
                            { label: 'Públicos', value: stats.globales },
                            { label: 'Favoritos', value: stats.favoritos },
                        ].map((k) => (
                            <Grid size={{ xs: 6, sm: 3 }} key={k.label}>
                                <Paper style={{ padding: 12, textAlign: 'center', borderRadius: 4 }}>
                                    <Typography variant="h6" style={{ fontWeight: 700 }}>{k.value}</Typography>
                                    <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                        <Grid size={12}>
                            <Typography variant="subtitle2" gutterBottom>Top autores</Typography>
                            {stats.porAutor.length === 0
                                ? <Typography variant="body2" color="text.secondary">Sin datos</Typography>
                                : stats.porAutor.map(([autor, cant]) => (
                                    <Box key={autor} display="flex" justifyContent="space-between" p={1} borderBottom="1px solid #eee">
                                        <Typography variant="body2">{autor}</Typography>
                                        <Typography variant="body2" color="text.secondary">{cant}</Typography>
                                    </Box>
                                ))
                            }
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatsOpen(false)} color="primary" variant="contained">Cerrar</Button>
                </DialogActions>
            </Dialog>
            {/* MODAL DE EDITOR */}
            <Dialog
                open={editorOpen}
                onClose={() => !saving && setEditorOpen(false)}
                sx={styles.editorDialog}
                maxWidth={false}
                fullScreen
            >
                <Box sx={styles.editorHeader}>
                    <TextField
                        sx={styles.editorTitle}
                        label="Título del documento"
                        value={editorTitulo}
                        onChange={e => {
                            setEditorTitulo(e.target.value);
                            setUnsavedChanges(true);
                        }}
                        fullWidth
                        InputProps={{ style: { color: "#fff" } }}
                    />

                    {puedeCrearGlobal && (
                        <FormControl variant="outlined" size="small" style={{ minWidth: 150, marginLeft: 12 }}>
                            <InputLabel style={{ color: "rgba(255,255,255,0.7)" }}>Visibilidad</InputLabel>
                            <Select
                                label="Visibilidad"
                                value={editorScope}
                                onChange={e => {
                                    setEditorScope(e.target.value);
                                    setUnsavedChanges(true);
                                }}
                                style={{ color: "#fff" }}
                            >
                                <MenuItem value="PRIVATE"><LockIcon fontSize="small" />&nbsp;Privado</MenuItem>
                                <MenuItem value="GLOBAL">
                                    <PublicIcon fontSize="small" />&nbsp;Público
                                </MenuItem>
                            </Select>
                        </FormControl>
                    )}

                    <FormControlLabel
                        control={
                            <Switch
                                checked={editorEstado === "PUBLICADO"}
                                onChange={e => {
                                    setEditorEstado(e.target.checked ? "PUBLICADO" : "BORRADOR");
                                    setUnsavedChanges(true);
                                }}
                                sx={{
                                    '& .MuiSwitch-switchBase': { color: '#fff' },
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.5)' },
                                    '& .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.3)' },
                                }}
                            />
                        }
                        label={editorEstado === "PUBLICADO" ? <><PublicIcon fontSize="small" /> Publicado</> : <><EditIcon fontSize="small" /> Borrador</>}
                        style={{ color: "#fff", marginLeft: 16 }}
                    />

                    <IconButton
                        onClick={() => {
                            if (unsavedChanges) {
                                setConfirmSalir({
                                    open: true,
                                    onConfirm: () => {
                                        setEditorOpen(false);
                                        setUnsavedChanges(false);
                                        setConfirmSalir({ open: false, onConfirm: null });
                                    }
                                });
                            } else {
                                setEditorOpen(false);
                            }
                        }}
                        style={{ color: "#fff" }}
                        disabled={saving}
                        size="large">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent sx={{ ...styles.editorContent, padding: 0 }}>
                    <Box p={2} bgcolor="#f0f3f7" borderBottom="1px solid #e0e0e0" display="grid" gridTemplateColumns="1fr 300px 300px" gap={12} alignItems="center">
                        <TextField
                            variant="outlined"
                            size="small"
                            label="Descripción"
                            value={editorDescripcion}
                            onChange={e => {
                                setEditorDescripcion(e.target.value);
                                setUnsavedChanges(true);
                            }}
                            fullWidth
                        />
                        <Autocomplete
                            options={categorias}
                            getOptionLabel={opt => opt?.nombreVisual || opt?.NombreVisual || opt?.Nombre || ""}
                            value={categorias.find(c => c.Id_Categoria === editorCategoriaId) || null}
                            onChange={(_, val) => {
                                const id = val ? val.Id_Categoria : null;
                                setEditorCategoriaId(id);
                                setEditorCategoria(val ? (val.Nombre || "") : "");   // por compatibilidad si seguís usando el texto
                                setUnsavedChanges(true);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    label="Categoría"
                                    fullWidth
                                />
                            )}
                        />

                    </Box>

                    {editorEstado !== "PUBLICADO" && (
                        <Box px={2} py={1} bgcolor="#fff8e1" borderBottom="1px solid #ffe082">
                            <Typography variant="caption" style={{ color: "#8d6e63" }}>
                                Documento Borrador, para que aparezca en Mis Escritos, publicalo.
                            </Typography>
                        </Box>
                    )}

                    <Box flex={1} overflow="auto" p={2}>
                        <TinyEditor
                            id="editor-escrito"
                            initialHtml={editorHtml}
                            onChange={(html) => {
                                setEditorHtml(html);
                                setUnsavedChanges(true);
                            }}
                            onReady={(api) => { editorApiRef.current = api; }}
                            onVariablesClick={() => setShowVarsBar(v => !v)}
                        />
                    </Box>
                </DialogContent>

                <Box sx={styles.editorFooter}>
                    <Box display="flex" alignItems="center" style={{ marginRight: 'auto' }}>
                        <Chip
                            icon={editorEstado === "PUBLICADO" ? <PublicIcon /> : <EditIcon />}
                            label={editorEstado}
                            color={editorEstado === "PUBLICADO" ? "primary" : "default"}
                            style={{ marginRight: 16 }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoSaveEnabled}
                                    onChange={(e) => {
                                        setAutoSaveEnabled(e.target.checked);
                                        localStorage.setItem('doc-autoSave', e.target.checked);
                                    }}
                                    size="small"
                                />
                            }
                            label="Auto-guardar"
                            style={{ marginRight: 16 }}
                        />

                        {autoSaveEnabled && lastSaved && (
                            <Typography variant="caption" color="text.secondary" style={{ marginRight: 16 }}>
                                Guardado: {lastSaved.toLocaleTimeString()}
                            </Typography>
                        )}

                        {unsavedChanges && (
                            <Chip
                                size="small"
                                label="Cambios sin guardar"
                                color="secondary"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Button
                            startIcon={<PrintIcon />}
                            onClick={() => editorApiRef.current?.print?.()}
                        >
                            Imprimir
                        </Button>

                        <Button
                            startIcon={<PictureAsPdfIcon />}
                            onClick={async () => {
                                const html = editorApiRef.current?.getContent?.() ?? editorHtml ?? "";
                                await exportHTMLtoPDF({ title: editorTitulo || "documento", html, marginMm: 18 });
                            }}
                        >
                            Exportar PDF
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={() => setEditorOpen(false)}
                            disabled={saving}
                        >
                            Cancelar
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={guardarEscrito}
                            disabled={saving || !editorTitulo.trim()}
                            sx={styles.primaryButton}
                        >
                            {saving ? "Guardando..." : (editorId && Number(editorId) > 0 ? "Guardar Cambios" : "Crear Documento")}
                        </Button>
                    </Box>
                </Box>

                {/* Panel lateral de variables — Drawer persistente (sin backdrop, no
                    bloquea el editor). z-index alto para que se vea por encima del
                    editor en pantalla completa (TinyMCE fullscreen usa z-index 1299). */}
                <Drawer
                    anchor="right"
                    open={showVarsBar}
                    variant="persistent"
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: 320,
                            zIndex: 1500,
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '-6px 0 18px rgba(0,0,0,0.22)',
                        },
                    }}
                >
                    <Box
                        px={2} py={1.25}
                        display="flex" alignItems="center" justifyContent="space-between"
                        sx={{ bgcolor: DOCS_PRIMARY, color: '#fff', flexShrink: 0 }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Variables disponibles {vars.length ? `(${vars.length})` : ''}
                        </Typography>
                        <IconButton size="small" onClick={() => setShowVarsBar(false)} sx={{ color: '#fff' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box p={2} sx={{ overflowY: 'auto', flex: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                            Hacé clic en una variable para insertarla en la posición del cursor.
                        </Typography>
                        {vars.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No hay variables disponibles para este documento.
                            </Typography>
                        ) : (() => {
                            // Agrupar variables por categoría
                            const grouped = vars.reduce((acc, v) => {
                                const cat = v.categoria || 'Otras';
                                if (!acc[cat]) acc[cat] = [];
                                acc[cat].push(v);
                                return acc;
                            }, {});

                            return Object.entries(grouped).map(([categoria, variables]) => (
                                <Box key={categoria} mb={2}>
                                    <Typography
                                        variant="subtitle2"
                                        style={{
                                            fontWeight: 700,
                                            color: DOCS_PRIMARY,
                                            marginBottom: 8,
                                            textTransform: 'uppercase',
                                            fontSize: 11,
                                            letterSpacing: 0.5
                                        }}
                                    >
                                        {categoria}
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                                        {variables.map(v => {
                                            const key = typeof v === 'string' ? v : v.key;
                                            const desc = typeof v === 'object' ? v.desc : null;

                                            return (
                                                <Tooltip
                                                    key={key}
                                                    title={desc || `Variable: {${key}}`}
                                                    arrow
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={`{${key}}`}
                                                        onClick={() => {
                                                            if (editorApiRef.current?.insert) {
                                                                editorApiRef.current.insert(`{${key}}`);
                                                            }
                                                        }}
                                                        sx={styles.varChip}
                                                        color="primary"
                                                        variant="outlined"
                                                        style={{
                                                            fontFamily: 'monospace',
                                                            fontSize: 11,
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ));
                        })()}
                    </Box>
                </Drawer>
            </Dialog>
            {/* DIÁLOGO DE CONFIRMACIÓN DE ELIMINACIÓN */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <DeleteOutlineIcon color="error" />
                        Confirmar eliminación
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de eliminar el documento "{itemToDelete?.titulo}"?
                    </Typography>
                    <Alert severity="warning" style={{ marginTop: 16 }}>
                        Esta acción no se puede deshacer
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="secondary"
                        variant="contained"
                        startIcon={<DeleteOutlineIcon />}
                    >
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* DIÁLOGO DE COMPARTIR */}
            <Dialog open={shareOpen} onClose={() => !shareSaving && setShareOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={8}>
                            <ShareIcon color="primary" />
                            <Typography variant="h6">Compartir escrito</Typography>
                        </Box>
                        <IconButton onClick={() => !shareSaving && setShareOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {shareDoc ? (shareDoc.titulo || "Escrito") : ""}
                    </Typography>
                </DialogTitle>

                <DialogContent dividers>
                    {shareLoading ? (
                        <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Typography variant="caption" color="text.secondary">
                                Buscá por nombre, apellido o usuario (min. 2 caracteres)
                            </Typography>

                            <Autocomplete
                                multiple
                                options={shareOptionsComputed}
                                value={[]}
                                renderTags={() => null}
                                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                                getOptionLabel={(o) => o?.nombre || String(o?.id || "")}
                                inputValue={shareQuery}
                                onInputChange={(e, v, reason) => {
                                    if (reason !== "reset") setShareQuery(v || "");
                                }}
                                onChange={(e, newValue) => {
                                    const last = newValue && newValue.length ? newValue[newValue.length - 1] : null;
                                    if (!last) return;
                                    const pid = Number(last.id);
                                    if (!Number.isFinite(pid)) return;

                                    setShareSelectedIds(prev => prev.includes(pid) ? prev : [...prev, pid]);
                                    setShareNameMap(prev => ({ ...prev, [pid]: last.nombre || String(pid) }));
                                    setShareQuery(""); // Limpiar búsqueda

                                    // Cerrar el dropdown
                                    setTimeout(() => {
                                        const input = document.querySelector('[aria-label="Buscar por apellido o nombre (Enter para agregar)"]');
                                        if (input) input.blur();
                                    }, 100);
                                }}
                                disableCloseOnSelect={false}
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                                filterSelectedOptions
                                onClose={() => setShareQuery("")}
                                renderOption={(props, opt) => {
                                    // MUI v5+: el primer arg son las props que hay que pasar al <li>.
                                    // Sin esto, MUI muestra "#_r_hh_-option-N" y tira warnings de key/button.
                                    const { key, ...rest } = props;
                                    return (
                                        <li key={key} {...rest}>
                                            <Box display="flex" alignItems="center" gap={8}>
                                                <PersonIcon fontSize="small" />
                                                <Typography variant="body2">{opt.nombre || `#${opt.id}`}</Typography>
                                            </Box>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        size="small"
                                        placeholder="Buscar por apellido o nombre (Enter para agregar)"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {shareSearchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />

                            {shareSelectedIds.length === 0 ? (
                                <Alert severity="info" style={{ marginTop: 12 }}>
                                    Aún no está compartido con nadie.
                                </Alert>
                            ) : (
                                <Box display="flex" flexWrap="wrap" gap={8} mt={1}>
                                    {shareSelectedIds.map(id => (
                                        <Chip
                                            key={id}
                                            label={shareNameMap[id] || String(id)}
                                            onDelete={() => setShareSelectedIds(prev => prev.filter(x => x !== id))}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setShareOpen(false)} disabled={shareSaving}>Cancelar</Button>
                    <Button
                        onClick={saveShare}
                        color="primary"
                        variant="contained"
                        startIcon={shareSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        disabled={shareSaving}
                    >
                        {shareSaving ? "Guardando..." : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* DIÁLOGO DE CONFIRMACIÓN DE DESCOMPARTIR */}
            <Dialog
                open={unshareConfirmOpen}
                onClose={() => setUnshareConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ExitToAppIcon color="primary" />
                        Dejar de compartir conmigo
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        ¿Querés dejar de ver el documento "{itemToUnshare?.titulo}"?
                    </Typography>
                    <Alert severity="info" style={{ marginTop: 16 }}>
                        Este documento ya no aparecerá en tu pestaña "Compartidos". El propietario del documento podrá volver a compartirlo contigo en cualquier momento.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnshareConfirmOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleUnshareMe}
                        color="primary"
                        variant="contained"
                        startIcon={<ExitToAppIcon />}
                    >
                        Dejar de compartir
                    </Button>
                </DialogActions>
            </Dialog>
            {/* MENÚ CONTEXTUAL */}
            <Menu
                open={contextMenu !== null}
                onClose={closeContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.y, left: contextMenu.x }
                        : undefined
                }
            >
                {contextItem && puedeRealizarAccion(contextItem, 'favorite') && (
                    <MenuItem onClick={() => {
                        contextItem && toggleFavorite(contextItem);
                        closeContextMenu();
                    }}>
                        {contextItem?.favorito ? (
                            <>
                                <StarBorderIcon fontSize="small" style={{ marginRight: 8 }} />
                                Quitar de favoritos
                            </>
                        ) : (
                            <>
                                <StarIcon fontSize="small" style={{ marginRight: 8 }} />
                                Agregar a favoritos
                            </>
                        )}
                    </MenuItem>
                )}

                {contextItem && puedeRealizarAccion(contextItem, 'share') && (
                    <MenuItem onClick={() => {
                        if (contextItem) openShareDialog(contextItem);
                        closeContextMenu();
                    }}>
                        <ShareIcon fontSize="small" style={{ marginRight: 8 }} />
                        Compartir
                    </MenuItem>
                )}

                {contextItem && puedeRealizarAccion(contextItem, 'edit') && (
                    <MenuItem onClick={() => {
                        contextItem && openEditar(contextItem);
                        closeContextMenu();
                    }}>
                        <EditIcon fontSize="small" style={{ marginRight: 8 }} />
                        Editar
                    </MenuItem>
                )}

                {contextItem && puedeRealizarAccion(contextItem, 'duplicate') && (
                    <MenuItem onClick={() => {
                        contextItem && duplicarComoPropio(contextItem);
                        closeContextMenu();
                    }}>
                        <FileCopyIcon fontSize="small" style={{ marginRight: 8 }} />
                        Duplicar
                    </MenuItem>
                )}

                {contextItem && puedeRealizarAccion(contextItem, 'unshare-self') && (
                    <MenuItem onClick={() => {
                        setItemToUnshare(contextItem);
                        setUnshareConfirmOpen(true);
                        closeContextMenu();
                    }}>
                        <ExitToAppIcon fontSize="small" style={{ marginRight: 8 }} />
                        Dejar de compartir conmigo
                    </MenuItem>
                )}

                {contextItem && puedeRealizarAccion(contextItem, 'delete') && (
                    <>
                        <Divider />
                        <MenuItem onClick={() => {
                            setItemToDelete(contextItem);
                            setDeleteConfirmOpen(true);
                            closeContextMenu();
                        }}>
                            <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8, color: "#f44336" }} />
                            <Typography color="error">Eliminar</Typography>
                        </MenuItem>
                    </>
                )}
            </Menu>
            {/* SpeedDial para acciones rápidas */}
            <SpeedDial
                ariaLabel="Acciones rápidas"
                sx={styles.speedDial}
                icon={<SpeedDialIcon />}
                onClose={() => setSpeedDialOpen(false)}
                onOpen={() => setSpeedDialOpen(true)}
                open={speedDialOpen}
                direction="up"
            >
                <SpeedDialAction
                    icon={<AddIcon />}
                    tooltipTitle="Nuevo documento"
                    tooltipOpen
                    tooltipPlacement="left"
                    FabProps={{ size: "small" }}
                    onClick={() => { setSpeedDialOpen(false); openNuevo("PRIVATE"); }}
                    sx={styles.sdActionSx}
                />
                <SpeedDialAction
                    icon={<PublicIcon />}
                    tooltipTitle="Nuevo documento Público"
                    tooltipOpen
                    tooltipPlacement="left"
                    FabProps={{ size: "small", disabled: !puedeCrearGlobal }}
                    onClick={() => {
                        setSpeedDialOpen(false);
                        if (puedeCrearGlobal) {
                            openNuevo("GLOBAL");
                        } else {
                            showSnack("Sin permiso para crear documentos públicos", "warning");
                        }
                    }}
                    sx={styles.sdActionSx}
                    style={{ display: puedeCrearGlobal ? 'flex' : 'none' }}
                />
                <SpeedDialAction
                    icon={<PublishIcon />}
                    tooltipTitle="Importar documento"
                    tooltipPlacement="left"
                    tooltipOpen
                    FabProps={{ size: "small" }}
                    onClick={() => { setSpeedDialOpen(false); handleImportDocument(); }}
                    sx={styles.sdActionSx}
                />
                <SpeedDialAction
                    icon={<GetAppIcon />}
                    tooltipTitle="Exportar seleccionado"
                    tooltipPlacement="left"
                    tooltipOpen
                    FabProps={{ size: "small" }}
                    onClick={() => { setSpeedDialOpen(false); handleExportSelected(); }}
                    disabled={!selected}
                    sx={styles.sdActionSx}
                />
                <SpeedDialAction
                    icon={<DescriptionIcon />}
                    tooltipTitle="Usar plantilla"
                    tooltipOpen
                    tooltipPlacement="left"
                    onClick={() => { setSpeedDialOpen(false); setShowTemplates(true); }}
                    sx={styles.sdActionSx}
                />
            </SpeedDial>
        </Box>

        {/* Diálogo de ayuda de teclado */}
        <KeyboardHelpDialog />
        {/* Diálogo de plantillas */}
        <TemplatesDialog />
        {/* Feedback de arrastre */}
        <DragFeedback />

        {/* Snackbar notificaciones */}
        <Snackbar
            open={snack.open}
            autoHideDuration={4000}
            onClose={() => setSnack(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert
                elevation={6}
                variant="filled"
                severity={snack.severity}
                onClose={() => setSnack(prev => ({ ...prev, open: false }))}
            >
                {snack.message}
            </Alert>
        </Snackbar>

        {/* Dialog confirmar salir sin guardar */}
        <Dialog
            open={confirmSalir.open}
            onClose={() => setConfirmSalir({ open: false, onConfirm: null })}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '4px' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ed6c02' }}>
                <WarningIcon /> Cambios sin guardar
            </DialogTitle>
            <DialogContent>
                <Typography>Hay cambios sin guardar. ¿Deseas salir sin guardar?</Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setConfirmSalir({ open: false, onConfirm: null })} color="inherit">
                    Cancelar
                </Button>
                <Button onClick={confirmSalir.onConfirm} variant="contained" color="warning">
                    Salir sin guardar
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}










